import { createSlice, nanoid, type PayloadAction } from '@reduxjs/toolkit';

import type { Answer } from '@/shared/api/types';

/**
 * The outbox: writes that must reach the server even if the connection drops.
 *
 * Answers and lesson positions are queued here and delivered in order by
 * outboxMiddleware. The queue is persisted, so closing the tab does not lose
 * work. Delivery is safe to repeat: answers carry an idempotency key, positions
 * only ever move forward on the server.
 */
interface Base {
  /** Doubles as the idempotency key of the request. */
  id: string;
  /** queued: not sent yet; sending: in flight; waiting: will retry when the network returns. */
  status: 'queued' | 'sending' | 'waiting';
}

export interface AnswerItem extends Base {
  kind: 'answer';
  attemptId: string;
  itemIndex: number;
  answer: Answer;
  lang: string;
}

export interface PositionItem extends Base {
  kind: 'position';
  lessonId: string;
  stepIndex: number;
}

export type OutboxItem = AnswerItem | PositionItem;

export interface OutboxState {
  items: OutboxItem[];
  /** Answers the server refused, by `answerKey`. Kept in memory only. */
  failures: Record<string, string>;
}

const initialState: OutboxState = { items: [], failures: {} };

export const answerKey = (attemptId: string, itemIndex: number) => `${attemptId}:${itemIndex}`;

const outboxSlice = createSlice({
  name: 'outbox',
  initialState,
  reducers: {
    answerQueued: {
      reducer(state, action: PayloadAction<AnswerItem>) {
        const item = action.payload;
        // A newer answer to the same item replaces one that has not been sent yet.
        state.items = state.items.filter(
          (other) =>
            other.kind !== 'answer' ||
            other.status === 'sending' ||
            answerKey(other.attemptId, other.itemIndex) !== answerKey(item.attemptId, item.itemIndex),
        );
        state.items.push(item);
        delete state.failures[answerKey(item.attemptId, item.itemIndex)];
      },
      prepare(payload: Omit<AnswerItem, 'id' | 'status' | 'kind'>) {
        return { payload: { ...payload, kind: 'answer' as const, id: nanoid(), status: 'queued' as const } };
      },
    },
    positionQueued: {
      reducer(state, action: PayloadAction<PositionItem>) {
        const item = action.payload;
        const sameLesson = state.items.filter(
          (other): other is PositionItem =>
            other.kind === 'position' && other.lessonId === item.lessonId,
        );
        // Only the furthest position matters: one already queued or in flight covers this one.
        if (sameLesson.some((other) => other.stepIndex >= item.stepIndex)) return;
        const pending = sameLesson.find((other) => other.status !== 'sending');
        if (pending) pending.stepIndex = item.stepIndex;
        else state.items.push(item);
      },
      prepare(payload: { lessonId: string; stepIndex: number }) {
        return { payload: { ...payload, kind: 'position' as const, id: nanoid(), status: 'queued' as const } };
      },
    },
    sendStarted(state, action: PayloadAction<string>) {
      const item = state.items.find((other) => other.id === action.payload);
      if (item) item.status = 'sending';
    },
    sendSucceeded(state, action: PayloadAction<string>) {
      state.items = state.items.filter((item) => item.id !== action.payload);
    },
    /** A transient failure: keep the item and try again later. */
    sendDeferred(state, action: PayloadAction<string>) {
      const item = state.items.find((other) => other.id === action.payload);
      if (item) item.status = 'waiting';
    },
    /** The server refused the request: retrying would not help. */
    sendRejected(state, action: PayloadAction<{ id: string; code: string }>) {
      const item = state.items.find((other) => other.id === action.payload.id);
      state.items = state.items.filter((other) => other.id !== action.payload.id);
      if (item?.kind === 'answer') {
        state.failures[answerKey(item.attemptId, item.itemIndex)] = action.payload.code;
      }
    },
    /** Ask the middleware to deliver whatever is queued. */
    flushRequested() {},
    outboxCleared() {
      return initialState;
    },
  },
});

export const {
  answerQueued,
  positionQueued,
  sendStarted,
  sendSucceeded,
  sendDeferred,
  sendRejected,
  flushRequested,
  outboxCleared,
} = outboxSlice.actions;
export const outboxReducer = outboxSlice.reducer;

export interface WithOutbox {
  outbox: OutboxState;
}

export function selectNextItem(state: WithOutbox): OutboxItem | undefined {
  return state.outbox.items.find((item) => item.status !== 'sending');
}

/** The furthest step of a lesson that is queued but has not reached the server yet. */
export function selectQueuedStep(state: WithOutbox, lessonId: string): number | undefined {
  const steps = state.outbox.items
    .filter((item): item is PositionItem => item.kind === 'position' && item.lessonId === lessonId)
    .map((item) => item.stepIndex);
  return steps.length > 0 ? Math.max(...steps) : undefined;
}

export interface AnswerDelivery {
  sending: boolean;
  waiting: boolean;
  /** Error code if the server refused the last answer to this item. */
  rejected: string | undefined;
}

export function selectAnswerDelivery(
  state: WithOutbox,
  attemptId: string,
  itemIndex: number,
): AnswerDelivery {
  const key = answerKey(attemptId, itemIndex);
  const item = state.outbox.items.find(
    (other) => other.kind === 'answer' && answerKey(other.attemptId, other.itemIndex) === key,
  );
  return {
    sending: item?.status === 'queued' || item?.status === 'sending',
    waiting: item?.status === 'waiting',
    rejected: state.outbox.failures[key],
  };
}

export function selectUndeliveredAnswers(state: WithOutbox, attemptId: string): number {
  return state.outbox.items.filter(
    (item) => item.kind === 'answer' && item.attemptId === attemptId,
  ).length;
}

/** What to persist: the queue, with in-flight items put back in line. */
export function persistableOutbox(state: OutboxState): OutboxState {
  return {
    items: state.items.map((item) =>
      item.status === 'queued' ? item : { ...item, status: 'queued' as const },
    ),
    failures: {},
  };
}

export function isOutboxState(value: unknown): value is OutboxState {
  if (typeof value !== 'object' || value === null) return false;
  const items = (value as Partial<OutboxState>).items;
  return (
    Array.isArray(items) &&
    items.every(
      (item: Partial<OutboxItem>) =>
        typeof item === 'object' &&
        item !== null &&
        typeof item.id === 'string' &&
        (item.kind === 'answer' || item.kind === 'position'),
    )
  );
}
